import React from "react";
import { ListItemProps } from "../../types";
import { ConfirmModal } from "../common";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { LockIcon, PencilIcon, TrashIcon } from "@phosphor-icons/react";
import "./list-item-clean.css";

const ListItemComponent: React.FC<ListItemProps> = ({
  item,
  canEdit,
  canReserve,
  isOwnList = false,
  onReserve,
  onCancelReservation,
  onEdit,
  onDelete,
  isSurpriseEvent = false,
  isLocked = false,
}) => {
  const {
    isOpen,
    config,
    isLoading,
    showConfirm,
    handleConfirm,
    handleCancel,
  } = useConfirmModal();

  const handleReserve = async () => {
    if (!onReserve) return;

    const confirmed = await showConfirm({
      title: "Conferma prenotazione",
      message: `Sei sicuro di voler prenotare "${item.name}"?`,
      confirmText: "Prenota",
      confirmVariant: "primary",
    });

    if (confirmed) {
      onReserve(item.id);
    }
  };

  const handleCancelReservation = async () => {
    if (!onCancelReservation) return;

    const confirmed = await showConfirm({
      title: "Annulla prenotazione",
      message: `Sei sicuro di voler annullare la prenotazione di "${item.name}"?`,
      confirmText: "Annulla prenotazione",
      confirmVariant: "warning",
    });

    if (confirmed) {
      onCancelReservation(item.id);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(item);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(item.id);
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return null;
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  return (
    <>
      <div
        className={`list-item ${item.isReserved ? "reserved" : ""} ${
          item.isObscured && !item.reservedByMe ? "obscured" : ""
        } ${item.reservedByMe ? "reserved-by-me" : ""}`}
      >
        <div className="item-content">
          {/* Blocco sinistro: Contenuto */}
          <div className="item-left">
            <div className="item-header">
              <h4 className="item-name">
                {item.isObscured && !item.reservedByMe && !isOwnList
                  ? "Regalo già prenotato"
                  : item.name}
              </h4>
              <div className="item-meta">
                {(!item.isObscured || item.reservedByMe || isOwnList) &&
                  item.link && (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-icon"
                      title="Visualizza prodotto"
                    >
                      ↗
                    </a>
                  )}
                {(!item.isObscured || item.reservedByMe || isOwnList) &&
                  item.price && (
                    <span className="item-price">
                      {formatPrice(item.price)}
                    </span>
                  )}
              </div>
            </div>

            {/* Descrizione subito sotto titolo e prezzo */}
            {(!item.isObscured || item.reservedByMe || isOwnList) &&
              item.description && (
                <div className="item-description">
                  <p>{item.description}</p>
                </div>
              )}

            {/* Messaggio per elementi oscurati - solo per liste di altri, mai per la propria lista */}
            {item.isObscured && !item.reservedByMe && !isOwnList && (
              <div className="item-description obscured-message">
                <p>
                  <em>
                    I dettagli di questo regalo non sono disponibili perchè già
                    prenotato da qualcun altro{" "}
                  </em>
                </p>
              </div>
            )}
          </div>

          {/* Blocco destro: Status e Azioni */}
          <div className="item-right">
            {/* Status di prenotazione */}
            {item.isReserved && (
              <div className="reservation-status">
                {item.reservedByMe ? (
                  <span className="reserved-by-me">Prenotato da te</span>
                ) : (
                  <span className="reserved-by-other">
                    Già prenotato{" "}
                    {/* Debug: isSurprise={isSurpriseEvent.toString()}, isOwn={isOwnList.toString()} */}
                  </span>
                )}
              </div>
            )}

            {/* Indicatore lista bloccata per proprietari in eventi sorpresa */}
            {isSurpriseEvent && isOwnList && isLocked && (
              <div className="locked-item-indicator">
                <span className="locked-status">
                  <LockIcon
                    size={14}
                    weight="fill"
                    style={{
                      verticalAlign: "middle",
                      marginRight: "4px",
                      marginBottom: "4px",
                    }}
                  />
                  Bloccato
                </span>
              </div>
            )}

            {/* Azioni */}
            <div className="item-actions">
              {!item.isObscured &&
                !item.isReserved &&
                !item.reservedByMe &&
                canReserve && (
                  <button
                    onClick={handleReserve}
                    className="reserve-button"
                    disabled={isOwnList}
                    title={
                      isOwnList
                        ? "Non puoi prenotare i tuoi stessi regali"
                        : undefined
                    }
                  >
                    Prenota
                  </button>
                )}

              {item.reservedByMe && !(isSurpriseEvent && isOwnList) && (
                <button
                  onClick={handleCancelReservation}
                  className="cancel-reservation-button"
                >
                  Annulla prenotazione
                </button>
              )}

              {(!item.isObscured || item.reservedByMe) &&
                canEdit &&
                !(isSurpriseEvent && isLocked) && (
                  <>
                    <button onClick={handleEdit} className="edit-button">
                      <PencilIcon
                        size={14}
                        weight="regular"
                        style={{ marginRight: "4px" }}
                      />
                      <span>Modifica</span>
                    </button>
                    <button onClick={handleDelete} className="delete-button">
                      <TrashIcon
                        size={14}
                        weight="regular"
                        style={{ marginRight: "4px" }}
                      />
                      <span>Elimina</span>
                    </button>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={isOpen}
        title={config?.title}
        message={config?.message || ""}
        confirmText={config?.confirmText}
        cancelText={config?.cancelText}
        confirmVariant={config?.confirmVariant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
    </>
  );
};

export default ListItemComponent;
